/**
 * Pure-function policy for post-call cleanup decisions in the local call
 * history.
 *
 * Browser equivalent of iOS `CallKitRecentsPolicy.swift`. The web has no
 * system "Recents" list (there is no CXProvider), but the app maintains its
 * own local, E2EE-at-rest call history. The same policy questions apply:
 *
 *   - Should this call be removed from local history after it ends? (Mirrors
 *     iOS `removeFromRecentsAfterEnd` driven by `suppressFromRecents`.)
 *   - Should the call duration be preserved? (Only meaningful for calls that
 *     actually connected.)
 *   - Should the app ensure the call is marked as "ended" in its own state
 *     machine? (Always yes — leaving a call in a non-terminal state causes
 *     stale UI, mirrors iOS "zombie call" concern.)
 *
 * The `suppressFromHistory` flag MUST be snapshotted at call-report time (the
 * moment the incoming call is presented to the user). Toggling the per-peer
 * setting mid-call MUST NOT retroactively un-suppress an entry the user wanted
 * hidden. Callers are responsible for passing the snapshot here, not the live
 * value.
 *
 * All decisions are pure functions with no side effects. No browser API is
 * imported.
 */

import type { PeerCallSettings } from "./CallDisplayPolicy";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * How the call ended.
 *
 * Web mirror of iOS `CallOutcome`.
 */
export type CallOutcome =
  | "answeredLocal"   // this device answered
  | "answeredRemote"  // another linked device answered
  | "missed"          // rang out without an answer
  | "declined"        // user explicitly declined
  | "failed";         // connection / signalling failure

/**
 * Post-call decisions the app should act on once the call has fully ended.
 *
 * Web mirror of iOS `CallKitRecentsCleanupDecision`.
 */
export interface CallHistoryCleanupDecision {
  /**
   * Always true. The call state machine MUST be driven to a terminal state
   * to avoid stale "call in progress" indicators — mirrors iOS
   * `scheduleSystemEndedCall: true`.
   */
  readonly markCallEnded: boolean;
  /**
   * True iff the user requested this peer's calls to be omitted from local
   * history. When true, the caller MUST delete or omit the history entry.
   */
  readonly removeFromHistoryAfterEnd: boolean;
  /**
   * True iff the call actually connected and the app should keep a duration
   * record in the local (E2EE-at-rest) call log. Missed / declined / failed
   * calls have no meaningful duration.
   */
  readonly preserveCallDurationInApp: boolean;
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

/**
 * Decide what to do with the local call history entry once the call ends.
 *
 * `settings` MUST be the snapshot taken at call-report time, not the live
 * per-peer settings (which the user may have changed during the call).
 *
 * Web mirror of iOS `CallKitRecentsPolicy.decideForCallEnded`.
 */
export function decideForCallEnded(
  settings: Pick<PeerCallSettings, "suppressFromHistory">,
  callOutcome: CallOutcome,
): CallHistoryCleanupDecision {
  const preserveDuration =
    callOutcome === "answeredLocal" || callOutcome === "answeredRemote";

  return {
    markCallEnded: true,
    removeFromHistoryAfterEnd: settings.suppressFromHistory,
    preserveCallDurationInApp: preserveDuration,
  };
}
